// Global
import {
  Link as JSSLink,
  LinkProps,
  LinkField,
  LinkFieldValue,
} from '@sitecore-content-sdk/nextjs';
import React, { forwardRef } from 'react';

// Local
import { CtaContentResultAnalyticsConfig } from 'components/helpers/Analytics/Cta/CtaAnalytics';
import { CtaProps } from 'components/helpers/Atoms/Cta/CtaButton';
import { SvgIcon } from 'components/helpers/SvgIcon';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { GtmEvent } from 'lib/utils/gtm-utils';
import { useFieldWithFallbacks } from 'lib/hooks/sitecore/useFieldWithFallbacks';
import CtaLink, { LinkContext } from 'components/helpers/Atoms/Cta/CtaLink';
import { useCtaClass } from 'components/helpers/Atoms/Cta/useCtaClass';

const INTERNAL_LINK_REGEX = /^\/|^\#/g;

export type LinkWrapperProps = Omit<LinkProps, 'field' | 'href'> &
  CtaProps & {
    field?: LinkField | LinkFieldValue;
    fallbacks?: (LinkField | LinkFieldValue | undefined)[];
    contentResultAnalytics?: CtaContentResultAnalyticsConfig;
    gtmEvent?: GtmEvent;
    srOnlyText?: string;
    suppressNewTabIcon?: boolean;
    linkContext?: LinkContext;
    analyticsHeader?: string;
    analyticsSubHeader?: string;
    analyticsClickText?: string;
  };

/**
 * This hook is used to render a link field.
 * This is needed because a component cannot return null.
 * This needs to be a hook because we need to use the useIsEditing hook to determine if we are in edit mode.
 * @param props - The props for the link wrapper.
 * @returns A JSX.Element if the field has a value, or if we are editing, otherwise null.
 */
export function useLinkWrapper(props: LinkWrapperProps): JSX.Element | null {
  const { field, fallbacks, editable = true } = props;
  const isEditing = useIsEditing() && editable;
  const { renderField } = useFieldWithFallbacks(field, fallbacks);

  const clonedField = structuredClone(renderField);

  // Standardize the field because it can either be LinkField or LinkFieldValue
  const fieldValue: LinkFieldValue = {
    ...((clonedField as LinkField)?.value ?? (clonedField as LinkFieldValue)),
  };

  const hasValue = !!fieldValue?.href;

  const shouldRender = hasValue || isEditing;
  if (!shouldRender) {
    return null;
  }
  return <LinkWrapper {...props} />;
}

const LinkWrapper = forwardRef<HTMLAnchorElement, LinkWrapperProps>(
  (props: LinkWrapperProps, ref): JSX.Element | null => {
    const { editable = true, field, fallbacks, ...nonSitecoreProps } = props;

    const isEditing = useIsEditing() && editable;

    const { renderField } = useFieldWithFallbacks(field, fallbacks);

    // If there's no field, don't render anything. This should only be the case
    // if the field itself is undefined, which should only happen if code is deployed
    // before data templates are published, so this is just a safety check
    if (!renderField) {
      return <></>;
    }

    // Clone the object so we don't modify the original.
    // This addresses some edge cases issues when the same link is rendered more than once
    // and we're modifying the link.  While it may not always be needed, it's safer to include
    const clonedField = structuredClone(renderField);

    /*
     * RENDERING
     */

    // If editing, always render
    if (isEditing) {
      return <EditModeLink {...props} ref={ref} field={clonedField} />;
    }

    // Standardize the field because it can either be LinkField or LinkFieldValue
    const fieldValue: LinkFieldValue = {
      ...((clonedField as LinkField)?.value ?? (clonedField as LinkFieldValue)),
    };

    return (
      <CtaLink
        {...nonSitecoreProps}
        url={fieldValue.href}
        text={fieldValue.text}
        target={fieldValue.target}
        title={fieldValue.title}
        anchor={fieldValue.anchor}
        querystring={fieldValue.querystring}
      />
    );
  }
);

LinkWrapper.displayName = 'LinkWrapper';

export default LinkWrapper;

type EditModeLinkProps = LinkWrapperProps;

const EditModeLink = forwardRef<HTMLAnchorElement, EditModeLinkProps>(
  (
    {
      ctaVariant,
      ctaIconAfter: ctaIcon,
      ctaStyle,
      ctaComponentClass,
      className,
      field,
      children,
      // Even though we don't use these fields, they are here so they get removed from the props
      // Otherwise we get a warning about unknown props on the JSSLink component
      srOnlyText: _srOnlyText,
      suppressNewTabIcon: _suppressNewTabIcon,
      linkContext: _linkContext,
      analyticsHeader: _analyticsHeader,
      analyticsSubHeader: _analyticsSubHeader,
      analyticsClickText: _analyticsClickText,
      ...props
    },
    ref
  ) => {
    const editableClonedField = field?.value ? (field as LinkField) : { value: { ...field } };

    const ctaClass = useCtaClass(ctaStyle, className, ctaComponentClass, ctaVariant);

    // The Link component, in its editable configuration, doesn't handle internal links correctly; i.e. it won't render the link text without a value for href.
    // For internal links, we'll forcibly set the href to '/' to work around the issue in a way that doesn't otherwise affect the editing experience.
    if (
      editableClonedField?.value?.linktype === 'internal' &&
      editableClonedField.value.href === ''
    )
      editableClonedField.value.href = '/';

    return (
      // Adding the CSS classes to a wrapping div so we can include the icon
      <div className={ctaClass}>
        <JSSLink
          {...props}
          field={editableClonedField}
          // className={`${title ? 'flex items-center' : ''}`}
          internalLinkMatcher={INTERNAL_LINK_REGEX}
          ref={ref}
        >
          {children}
        </JSSLink>
        {/* When in edit mode we cannot render anything inside the Link tag (cause duplicate link), 
          but we can rendering it outside of the link and move the styling to a parent div */}
        {ctaIcon && <SvgIcon icon={ctaIcon} size="xs" />}
      </div>
    );
  }
);

EditModeLink.displayName = 'EditModeLink';
